import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { Signal } from '@/types/signal';
import { getUpdatedNodes } from '@/util/application';
import { Button, Stack } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import { FC, useState } from 'react';
import SignalListItem from './SignalListItem';
import SignalListItemEdit from './SignalListItemEdit';

const SignalManager: FC = () => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { confirm } = useDialogs();

  const [openEdit, setOpenEdit] = useState(false);

  const confirmExists: ConfirmOptions = {
    title: 'Event already exists',
    description: `This user event already exists`,
    confirmationText: 'Ok',
    hideCancelButton: true
  };

  const handleOnSave = (newSignal: Signal, oldName?: string) => {
    let updatedSignals = application.signals ? structuredClone(application.signals) : [];

    if (!oldName) {
      updatedSignals[updatedSignals.length] = {
        id: newSignal.id,
        name: newSignal.name,
        description: newSignal.description,
        substart: newSignal.substart
      };
    } else {
      updatedSignals = updatedSignals.map((signal) => {
        if (signal.name === oldName) {
          return {
            id: newSignal.id,
            name: newSignal.name,
            description: newSignal.description,
            substart: newSignal.substart
          };
        } else {
          return signal;
        }
      });
    }
    if (
      updatedSignals.filter((signal) => signal.name.toLocaleLowerCase() === newSignal.name.toLocaleLowerCase()).length >
      1
    ) {
      confirm(confirmExists);
      return;
    }

    setApplication({ ...application, signals: updatedSignals });
    setUpdateNodes(getUpdatedNodes(newSignal.id, 'signal', application.blocks.details));
  };

  return (
    <Stack gap={1} alignItems='start'>
      <List sx={{ width: '100%' }}>
        {application.signals
          ?.sort((a, b) => a.name.localeCompare(b.name))
          .map((signal, index) => {
            return (
              <SignalListItem
                key={`prompt.${signal.id}.${index}`}
                signal={signal}
                onSave={(newSignal) => handleOnSave(newSignal, signal.name)}
                onDelete={() => {
                  const removed = application.signals?.filter((keep) => keep.name !== signal.name);
                  setApplication({
                    ...application,
                    signals: removed
                  });
                  setUpdateNodes(getUpdatedNodes(signal.id, 'signal', application.blocks.details));
                }}
              />
            );
          })}
      </List>
      {openEdit && (
        <SignalListItemEdit onClose={() => setOpenEdit(false)} onSave={(newSignal) => handleOnSave(newSignal)} />
      )}
      <Button variant='contained' color='primary' sx={{ ml: 2 }} onClick={() => setOpenEdit(true)}>
        Add new
      </Button>
    </Stack>
  );
};

export default SignalManager;
